import React, { useRef, useEffect, useState, useCallback } from 'react';

/**
 * CustomVisualSandbox (GAP-7.1 runtime)
 * =====================================
 * Runs tenant-authored visualization JS inside a hardened <iframe>:
 *   sandbox="allow-scripts"  →  scripts run, but the frame has a NULL origin,
 *   so it CANNOT read parent cookies/localStorage, touch the parent DOM, or
 *   issue same-origin credentialed requests. Data flows in one direction via
 *   postMessage; the author never sees auth state.
 *
 * Author SDK contract (runs inside the frame):
 *   inzara.render(function (ctx) {
 *     // ctx.container : <div> to draw into
 *     // ctx.data      : array of row objects
 *     // ctx.config    : values from the visual's config schema
 *     // ctx.width / ctx.height : pixels
 *     // ctx.theme     : { text, grid, colors[] }
 *     // ctx.formatNumber(n) : helper
 *   });
 */

function buildSrcDoc(jsCode) {
  // The bootstrap defines the `inzara` global, wires the postMessage channel,
  // and re-invokes the author's render callback whenever new data arrives.
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<style>
  html,body{margin:0;padding:0;height:100%;width:100%;overflow:hidden;
    font-family:ui-sans-serif,system-ui,-apple-system,sans-serif;}
  #root{height:100%;width:100%;}
  .inzara-err{color:#ef4444;font-size:12px;padding:8px;white-space:pre-wrap;}
</style></head>
<body><div id="root"></div>
<script>
(function(){
  var _cb = null, _last = null;
  var root = document.getElementById('root');
  function post(type, payload){ try{ parent.postMessage(Object.assign({source:'inzara-sandbox',type:type}, payload||{}), '*'); }catch(e){} }
  window.inzara = {
    render: function(cb){ _cb = cb; if(_last) draw(_last); },
    formatNumber: function(n){ if(n==null||isNaN(n)) return String(n); return Number(n).toLocaleString(); }
  };
  function draw(ctx){
    if(!_cb){ return; }
    root.innerHTML='';
    var inner = document.createElement('div');
    inner.style.height='100%'; inner.style.width='100%';
    root.appendChild(inner);
    try{
      _cb({ container: inner, data: ctx.data||[], config: ctx.config||{},
            width: ctx.width||inner.clientWidth, height: ctx.height||inner.clientHeight,
            theme: ctx.theme||{}, formatNumber: window.inzara.formatNumber });
      post('rendered');
    }catch(e){
      root.innerHTML = '<div class="inzara-err">Render error: '+(e && e.message ? e.message : e)+'</div>';
      post('error', { message: String(e && e.message ? e.message : e) });
    }
  }
  window.addEventListener('message', function(ev){
    var d = ev.data;
    if(!d || d.source!=='inzara-host') return;
    if(d.type==='data'){ _last = d.payload; draw(d.payload); }
  });
  // Surface author syntax errors immediately.
  try {
    ${jsCode}
  } catch(e){
    root.innerHTML = '<div class="inzara-err">Script error: '+(e && e.message ? e.message : e)+'</div>';
    post('error', { message: String(e && e.message ? e.message : e) });
  }
  post('ready');
})();
</script></body></html>`;
}

export function CustomVisualSandbox({ jsCode, data, config, theme, height = 340 }) {
  const iframeRef = useRef(null);
  const wrapRef = useRef(null);
  const readyRef = useRef(false);
  const [error, setError] = useState(null);
  const srcDoc = React.useMemo(() => buildSrcDoc(jsCode || ''), [jsCode]);

  const sendData = useCallback(() => {
    const frame = iframeRef.current;
    if (!frame || !frame.contentWindow || !readyRef.current) return;
    const rect = wrapRef.current?.getBoundingClientRect();
    frame.contentWindow.postMessage({
      source: 'inzara-host',
      type: 'data',
      payload: {
        data: data || [],
        config: config || {},
        theme: theme || {},
        width: Math.round(rect?.width || 0),
        height: Math.round(rect?.height || height),
      },
    }, '*');
  }, [data, config, theme, height]);

  useEffect(() => {
    readyRef.current = false;
    setError(null);
    const onMsg = (ev) => {
      const d = ev.data;
      if (!d || d.source !== 'inzara-sandbox') return;
      if (ev.source !== iframeRef.current?.contentWindow) return;
      if (d.type === 'ready') { readyRef.current = true; sendData(); }
      else if (d.type === 'error') { setError(d.message || 'Unknown error'); }
      else if (d.type === 'rendered') { setError(null); }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [srcDoc, sendData]);

  useEffect(() => { sendData(); }, [sendData]);

  useEffect(() => {
    if (!wrapRef.current || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => sendData());
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [sendData]);

  return (
    <div ref={wrapRef} className="relative w-full" style={{ height }} data-testid="custom-visual-sandbox">
      <iframe
        ref={iframeRef}
        title="custom-visual"
        sandbox="allow-scripts"
        srcDoc={srcDoc}
        className="w-full h-full border-0"
        style={{ background: 'transparent' }}
        data-testid="custom-visual-iframe"
      />
      {error && (
        <div className="absolute bottom-1 left-1 right-1 text-[10px] text-red-500 bg-red-500/10 rounded px-2 py-1 truncate" data-testid="custom-visual-error">
          {error}
        </div>
      )}
    </div>
  );
}

export default CustomVisualSandbox;
